import React from 'react';
import { X, Download, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker - use local worker for Vite compatibility
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?url';
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
}

export function DocumentPreviewModal({
  isOpen,
  onClose,
  documentUrl,
  fileName,
  fileSize,
  mimeType
}: DocumentPreviewModalProps) {
  const [zoom, setZoom] = React.useState(1);
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [contentLoaded, setContentLoaded] = React.useState(false);
  const [contentError, setContentError] = React.useState(false);
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [pdfScale, setPdfScale] = React.useState(1.0);
  
  // Determine if this is an image or PDF
  const isImage = mimeType?.startsWith('image/') || 
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
      fileName.toLowerCase().split('.').pop() || ''
    );
  const isPDF = mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

  // Reset zoom, position, and loading states when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setContentLoaded(false);
      setContentError(false);
      setPageNumber(1);
      setPdfScale(1.0);
      setNumPages(null);
    }
  }, [isOpen, documentUrl]);


  // Handle keyboard events
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        if (isImage) {
          setZoom(prev => Math.min(prev * 1.2, 5));
        } else if (isPDF) {
          handlePdfZoomIn();
        }
      } else if (e.key === '-') {
        e.preventDefault();
        if (isImage) {
          setZoom(prev => Math.max(prev / 1.2, 0.1));
        } else if (isPDF) {
          handlePdfZoomOut();
        }
      } else if (e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      } else if (isPDF && e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevPage();
      } else if (isPDF && e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextPage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleResetZoom = () => {
    if (isImage) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    } else if (isPDF) {
      setPdfScale(1.0);
    }
  };

  const handlePdfZoomIn = () => {
    setPdfScale(prev => Math.min(prev * 1.2, 3.0));
  };

  const handlePdfZoomOut = () => {
    setPdfScale(prev => Math.max(prev / 1.2, 0.5));
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        // Close modal if clicking on the backdrop (not the modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm" />
      
      {/* Modal Content */}
      <div 
        className="relative z-10 max-w-[95vw] max-h-[95vh] bg-white rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => {
          // Prevent backdrop click when clicking modal content
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {fileName}
            </h3>
            {(fileSize || mimeType) && (
              <p className="text-sm text-gray-600">
                {fileSize && formatFileSize(fileSize)}
                {fileSize && mimeType && ' • '}
                {mimeType}
              </p>
            )}
          </div>
          
          {/* Controls */}
          <div className="flex items-center space-x-2 ml-4">
            {/* PDF Page Navigation */}
            {isPDF && numPages && numPages > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToPrevPage();
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
                  title="Previous Page (←)"
                  type="button"
                  disabled={pageNumber <= 1}
                >
                  <ChevronLeft size={18} />
                </button>
                
                <span className="text-sm text-gray-600 min-w-[4rem] text-center">
                  {pageNumber} / {numPages}
                </span>
                
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToNextPage();
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
                  title="Next Page (→)"
                  type="button"
                  disabled={pageNumber >= numPages}
                >
                  <ChevronRight size={18} />
                </button>
                
                <div className="w-px h-6 bg-gray-300 mx-2" />
              </>
            )}
            
            {/* Zoom Controls */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isImage) {
                  handleZoomOut();
                } else if (isPDF) {
                  handlePdfZoomOut();
                }
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
              title="Zoom Out (-)"
              type="button"
            >
              <ZoomOut size={18} />
            </button>
            
            <span className="text-sm text-gray-600 min-w-[4rem] text-center">
              {Math.round((isImage ? zoom : pdfScale) * 100)}%
            </span>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isImage) {
                  handleZoomIn();
                } else if (isPDF) {
                  handlePdfZoomIn();
                }
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
              title="Zoom In (+)"
              type="button"
            >
              <ZoomIn size={18} />
            </button>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResetZoom();
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
              title="Reset Zoom (0)"
              type="button"
            >
              Reset
            </button>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownload();
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
              title="Download"
              type="button"
            >
              <Download size={18} />
            </button>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
              title="Close (Esc)"
              type="button"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        {/* Document Container */}
        <div 
          className="relative overflow-hidden bg-gray-100"
          style={{ 
            width: '80vw', 
            height: '70vh',
            cursor: isImage && zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onMouseDown={isImage ? handleMouseDown : undefined}
          onMouseMove={isImage ? handleMouseMove : undefined}
          onMouseUp={isImage ? handleMouseUp : undefined}
          onMouseLeave={isImage ? handleMouseUp : undefined}
        >
          {/* Image Content */}
          {isImage && (
            <div className="flex items-center justify-center w-full h-full">
              <img
                src={documentUrl}
                alt={fileName}
                className={`max-w-full max-h-full object-contain transition-transform duration-200 ${contentLoaded ? 'block' : 'hidden'}`}
                style={{
                  transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                  transformOrigin: 'center center'
                }}
                draggable={false}
                onLoad={() => {
                  console.log('✅ Image loaded successfully:', documentUrl);
                  setContentLoaded(true);
                  setContentError(false);
                }}
                onError={() => {
                  console.error('❌ Failed to load image:', documentUrl);
                  setContentError(true);
                  setContentLoaded(false);
                }}
              />
            </div>
          )}
          
          {/* PDF Content */}
          {isPDF && (
            <div className="flex items-center justify-center w-full h-full bg-white overflow-auto">
              <Document
                file={documentUrl}
                onLoadSuccess={({ numPages }) => {
                  console.log('✅ PDF loaded successfully:', documentUrl, 'Pages:', numPages);
                  setNumPages(numPages);
                  setContentLoaded(true);
                  setContentError(false);
                }}
                onLoadError={(error) => {
                  console.error('❌ Failed to load PDF:', documentUrl, error);
                  setContentError(true);
                  setContentLoaded(false);
                }}
                loading={
                  <div className="flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading PDF...</p>
                    </div>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center p-8">
                    <div className="text-center text-red-600">
                      <p className="mb-4">Failed to load PDF</p>
                      <div className="space-x-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(documentUrl, '_blank');
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          type="button"
                        >
                          Open in New Tab
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDownload();
                          }}
                          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                          type="button"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={pdfScale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            </div>
          )}
          
          {/* Other document types */}
          {!isImage && !isPDF && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-600">
                <p className="text-lg mb-4">Preview not available</p>
                <p className="text-sm mb-4">This document type cannot be previewed in the browser.</p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownload();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  type="button"
                >
                  Download to View
                </button>
              </div>
            </div>
          )}
          
          {/* Loading fallback */}
          {!contentLoaded && !contentError && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 bg-gray-100">
              <div className="text-center">
                <p className="text-lg mb-2">Loading {isImage ? 'image' : isPDF ? 'PDF' : 'document'}...</p>
                <p className="text-sm">Please wait while the content loads.</p>
              </div>
            </div>
          )}
          
          {/* Error fallback */}
          {contentError && (
            <div className="absolute inset-0 flex items-center justify-center text-red-500 bg-gray-100">
              <div className="text-center">
                <p className="text-lg mb-2">Failed to load {isImage ? 'image' : isPDF ? 'PDF' : 'document'}</p>
                <p className="text-sm">The content could not be displayed.</p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setContentError(false);
                    setContentLoaded(false);
                    // Force content reload
                    if (isImage) {
                      const img = document.querySelector(`img[src*="${documentUrl}"]`) as HTMLImageElement;
                      if (img) {
                        img.src = documentUrl + '?retry=' + Date.now();
                      }
                    }
                  }}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  type="button"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer with keyboard shortcuts */}
        <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-600">
          <span>Keyboard shortcuts: </span>
          <span className="font-mono">+/-</span> zoom, 
          <span className="font-mono ml-1">0</span> reset, 
          {isPDF && numPages && numPages > 1 && (
            <>
              <span className="font-mono ml-1">←/→</span> pages, 
            </>
          )}
          <span className="font-mono ml-1">Esc</span> close
          {isImage && zoom > 1 && <span className="ml-2">• Drag to pan</span>}
        </div>
      </div>
    </div>
  );
}

// Backward compatibility export
export const ImagePreviewModal = DocumentPreviewModal;
